const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

const readString = (object, key) => {
  const value = object?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : "";
};

const normalizeEnvironmentLabel = (value) => {
  const label = asObject(value);
  const environmentId = readString(label, "environmentId");
  const displayName = readString(label, "displayName");
  if (!environmentId || !displayName) return null;

  const description = readString(label, "description");
  return {
    environmentId,
    displayName,
    ...(description ? { description } : {}),
  };
};

const normalizeSubElementLabel = (value) => {
  const label = asObject(value);
  const key = readString(label, "key");
  const displayName = readString(label, "displayName");
  if (!key || !displayName) return null;

  const description = readString(label, "description");
  return {
    key,
    displayName,
    ...(description ? { description } : {}),
  };
};

const normalizeElementLabel = (value) => {
  const label = asObject(value);
  const elementId = readString(label, "elementId");
  const displayName = readString(label, "displayName");
  if (!elementId || !displayName) return null;

  const description = readString(label, "description");
  const subElementLabels = Array.isArray(label.subElementLabels)
    ? label.subElementLabels.map(normalizeSubElementLabel).filter(Boolean)
    : [];
  return {
    elementId,
    displayName,
    ...(description ? { description } : {}),
    ...(subElementLabels.length ? { subElementLabels } : {}),
  };
};

export const getGuideEnvironmentLabels = (guideData) => {
  if (!Array.isArray(guideData?.environmentLabels)) return [];
  return guideData.environmentLabels.map(normalizeEnvironmentLabel).filter(Boolean);
};

export const getEffectiveEnvironmentLabels = (experienceData, guideData) => {
  const experienceLabels = Array.isArray(experienceData?.environmentLabels)
    ? experienceData.environmentLabels.map(normalizeEnvironmentLabel).filter(Boolean)
    : [];
  return experienceLabels.length
    ? experienceLabels
    : getGuideEnvironmentLabels(guideData);
};

export const getGuideElementLabels = (guideData) => {
  if (!Array.isArray(guideData?.elementLabels)) return [];
  return guideData.elementLabels.map(normalizeElementLabel).filter(Boolean);
};

const getElementIdCandidates = (elementData) => {
  const nestedElement = asObject(elementData?.elementData);
  return [
    elementData?.id,
    elementData?.elementId,
    elementData?.elementID,
    nestedElement?.id,
    nestedElement?.elementId,
    nestedElement?.elementID,
  ]
    .filter((value) => typeof value === "string" && value.trim())
    .map((value) => value.trim());
};

const getSubElementKeyCandidates = (elementData) => {
  const nestedElement = asObject(elementData?.elementData);
  return [elementData?.key, elementData?.subElementKey, nestedElement?.key]
    .filter((value) => typeof value === "string" && value.trim())
    .map((value) => value.trim());
};

export const applyGuideElementLabel = (elementData, elementLabels) => {
  if (!elementData || !Array.isArray(elementLabels) || !elementLabels.length) {
    return elementData;
  }

  const subElementKeys = new Set(getSubElementKeyCandidates(elementData));
  const subElementLabel = elementLabels
    .flatMap((candidate) => candidate.subElementLabels || [])
    .find((candidate) => subElementKeys.has(candidate.key));
  const elementIds = new Set(getElementIdCandidates(elementData));
  const label =
    subElementLabel ||
    elementLabels.find((candidate) => elementIds.has(candidate.elementId));
  if (!label) return elementData;

  return {
    ...elementData,
    dispatchedName: label.displayName,
    ...(label.description ? { dispatchedDescription: label.description } : {}),
  };
};
